import React from 'react';
import { Bell, Monitor, Smartphone, Trash2 } from 'lucide-react';
import { PWAInstallAdvice } from '../../components/PWAInstallAdvice';
import { PushNotificationToggle } from '../../components/PushNotificationToggle';
import { Banner, Card, EmptyState, Field, IconButton, Section, Select } from '../../components/ui';

export interface DeviceLike {
  userAgent: string;
  lastActive: number;
}

export interface NotificationsSectionProps {
  devices: Record<string, DeviceLike> | undefined;
  isLoading: boolean;
  onRemoveDevice: (deviceId: string) => void;
  feedMaxAgeDays: number;
  onFeedMaxAgeChange: (days: number) => void;
}

function isMobileDevice(userAgent: string): boolean {
  const ua = userAgent.toLowerCase();
  return ua.includes('mobi') || ua.includes('android') || ua.includes('iphone');
}

function deviceLabel(userAgent: string): string {
  return userAgent.split(')')[0].split('(')[1] || 'Unbekanntes Gerät';
}

export const NotificationsSection: React.FC<NotificationsSectionProps> = ({
  devices,
  isLoading,
  onRemoveDevice,
  feedMaxAgeDays,
  onFeedMaxAgeChange,
}) => {
  const deviceEntries = devices ? Object.entries(devices) : [];

  return (
    <div className="flex flex-col">
      <Section
        title="Geräte"
        icon={<Bell className="h-5 w-5" aria-hidden="true" />}
        description="Verwalte die Geräte, auf denen du Push-Meldungen direkt im Browser erhältst."
      >
        <div className="flex flex-col gap-4">
          <PWAInstallAdvice />

          {deviceEntries.length === 0 ? (
            <EmptyState icon={<Smartphone className="h-6 w-6" aria-hidden="true" />} title="Noch kein Gerät registriert">
              Registriere dieses Gerät unten, um Push-Meldungen zu erhalten.
            </EmptyState>
          ) : (
            <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {deviceEntries.map(([deviceId, device]) => {
                const Icon = isMobileDevice(device.userAgent) ? Smartphone : Monitor;
                return (
                  <Card as="li" key={deviceId} padding="sm" className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <Icon className="h-6 w-6 shrink-0 text-muted" aria-hidden="true" />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-ink">{deviceLabel(device.userAgent)}</p>
                        <p className="text-sm text-muted">
                          Zuletzt aktiv: {new Date(device.lastActive).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    </div>
                    <IconButton
                      label="Gerät entfernen"
                      icon={<Trash2 className="h-4 w-4" />}
                      variant="ghost"
                      onClick={() => onRemoveDevice(deviceId)}
                      disabled={isLoading}
                    />
                  </Card>
                );
              })}
            </ul>
          )}

          <div>
            <p className="mb-2 font-medium text-ink">Dieses Gerät registrieren</p>
            <PushNotificationToggle />
          </div>

          <Banner tone="info">
            Meldungseinstellungen legst du für jede Suche einzeln fest. Diese Liste zeigt nur deine registrierten
            Geräte für Browser-Push.
          </Banner>
        </div>
      </Section>

      <Section title="Treffer" description="Lege fest, wie alte Inserate in den Treffern angezeigt werden.">
        <Field
          label="Maximales Inserate-Alter"
          htmlFor="feedMaxAge"
          hint="Ältere Inserate werden in den Treffern ausgeblendet. Die Einstellung wird lokal auf diesem Gerät gespeichert."
          className="max-w-xs"
        >
          <Select
            id="feedMaxAge"
            value={feedMaxAgeDays}
            onChange={(e) => onFeedMaxAgeChange(parseInt(e.target.value, 10))}
            aria-describedby="feedMaxAge-hint"
          >
            <option value={0}>Kein Limit (alle anzeigen)</option>
            <option value={1}>1 Tag</option>
            <option value={3}>3 Tage</option>
            <option value={7}>7 Tage</option>
            <option value={14}>14 Tage</option>
            <option value={30}>30 Tage</option>
          </Select>
        </Field>
      </Section>
    </div>
  );
};
